import React, { useState, useEffect, useRef } from 'react';
import { Copy, Check, MessageSquare } from 'lucide-react';
import { PLAY_STORE_URL, SHARE_TEXT as SHARE_TEXT_APP, shareAppWithImage, shareUrls } from '../services/shareApp';

const APP_URL_DEFAULT = PLAY_STORE_URL;
const SHARE_TEXT_DEFAULT = SHARE_TEXT_APP;

const ShareButton = ({
  url,
  text,
  title = 'Hypnose clé',
  label = 'Partager',
  dropdownTitle = 'Partager Hypnose clé',
  testid = 'share-btn',
  variant = 'default', // 'default' | 'compact'
}) => {
  const APP_URL = url || APP_URL_DEFAULT;
  const SHARE_TEXT = text || SHARE_TEXT_DEFAULT;
  const [showShare, setShowShare] = useState(false);
  const [copied, setCopied] = useState(false);
  const shareRef = useRef(null);

  useEffect(() => {
    if (!showShare) return;
    const handleClickOutside = (e) => {
      if (shareRef.current && !shareRef.current.contains(e.target)) setShowShare(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showShare]);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(APP_URL).then(() => {
      setCopied(true);
      setTimeout(() => { setCopied(false); setShowShare(false); }, 1500);
    });
  };

  const handleShareWhatsApp = () => {
    window.open(shareUrls.whatsapp, '_blank');
    setShowShare(false);
  };

  const handleShareInstagram = () => {
    navigator.clipboard.writeText(SHARE_TEXT);
    window.open('https://www.instagram.com/', '_blank');
    setShowShare(false);
  };

  const handleNativeShare = async () => {
    // Image + texte + lien Play Store via la feuille de partage du téléphone
    const handled = await shareAppWithImage();
    if (handled) setShowShare(false);
    return handled;
  };

  const handleShareFacebook = () => {
    window.open(shareUrls.facebook, '_blank');
    setShowShare(false);
  };

  const handleShareTelegram = () => {
    window.open(shareUrls.telegram, '_blank');
    setShowShare(false);
  };

  const handleShareTwitter = () => {
    window.open(shareUrls.twitter, '_blank');
    setShowShare(false);
  };

  const handleShareSms = () => {
    window.location.href = shareUrls.sms;
    setShowShare(false);
  };

  const handleShareEmail = () => {
    window.location.href = shareUrls.email;
    setShowShare(false);
  };

  const handleClick = async () => {
    // Try native Web Share API first (iOS Safari, Android Chrome)
    const handled = await handleNativeShare();
    if (!handled) setShowShare(!showShare);
  };

  return (
    <div className="relative inline-block" ref={shareRef}>
      <button
        onClick={handleClick}
        className={
          variant === 'compact'
            ? "flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-sm transition-all hover:scale-105 shadow-lg shadow-amber-500/20 hover:shadow-amber-500/40"
            : "flex items-center gap-2.5 px-6 py-3 rounded-full font-semibold text-base transition-all hover:scale-105 shadow-lg shadow-amber-500/20 hover:shadow-amber-500/40"
        }
        style={{
          background: 'linear-gradient(135deg, #b45309, #d97706, #f59e0b, #d97706, #b45309)',
          color: '#fff',
          border: '1.5px solid rgba(251, 191, 36, 0.5)',
        }}
        data-testid={testid}
      >
        <img src="/icons/logo-main-cle-5.png" alt="" className={variant === 'compact' ? "w-5 h-5 rounded object-cover" : "w-6 h-6 rounded object-cover"} />
        <span>{label}</span>
      </button>

      {showShare && (
        <div className="absolute left-1/2 -translate-x-1/2 top-full mt-3 w-72 bg-slate-900/98 backdrop-blur-xl rounded-xl border border-amber-500/20 shadow-2xl shadow-amber-500/10 overflow-hidden z-50" data-testid="share-dropdown">
          <div className="p-3 border-b border-white/5 text-center">
            <p className="text-amber-400/80 text-xs font-medium uppercase tracking-wider">{dropdownTitle}</p>
          </div>
          <div className="p-2 max-h-80 overflow-y-auto">
            <button
              onClick={handleShareWhatsApp}
              className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg hover:bg-green-500/10 text-white/80 hover:text-green-400 transition-all"
              data-testid="share-whatsapp"
            >
              <svg className="w-5 h-5 text-green-500" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
              <span className="font-medium text-sm">WhatsApp</span>
            </button>
            <button
              onClick={handleShareInstagram}
              className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg hover:bg-pink-500/10 text-white/80 hover:text-pink-400 transition-all"
              data-testid="share-instagram"
            >
              <svg className="w-5 h-5 text-pink-500" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
              <span className="font-medium text-sm">Instagram</span>
            </button>
            <button
              onClick={handleShareFacebook}
              className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg hover:bg-blue-500/10 text-white/80 hover:text-blue-400 transition-all"
              data-testid="share-facebook"
            >
              <svg className="w-5 h-5 text-blue-500" viewBox="0 0 24 24" fill="currentColor"><path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z"/></svg>
              <span className="font-medium text-sm">Facebook</span>
            </button>
            <button
              onClick={handleShareTelegram}
              className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg hover:bg-sky-500/10 text-white/80 hover:text-sky-400 transition-all"
              data-testid="share-telegram"
            >
              <svg className="w-5 h-5 text-sky-500" viewBox="0 0 24 24" fill="currentColor"><path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/></svg>
              <span className="font-medium text-sm">Telegram</span>
            </button>
            <button
              onClick={handleShareTwitter}
              className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg hover:bg-white/10 text-white/80 hover:text-white transition-all"
              data-testid="share-twitter"
            >
              <svg className="w-5 h-5 text-white/80" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
              <span className="font-medium text-sm">X (Twitter)</span>
            </button>
            <button
              onClick={handleShareSms}
              className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg hover:bg-teal-500/10 text-white/80 hover:text-teal-300 transition-all"
              data-testid="share-sms"
            >
              <MessageSquare className="w-5 h-5 text-teal-400" />
              <span className="font-medium text-sm">SMS</span>
            </button>
            <button
              onClick={handleShareEmail}
              className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg hover:bg-amber-500/10 text-white/80 hover:text-amber-300 transition-all"
              data-testid="share-email"
            >
              <svg className="w-5 h-5 text-amber-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
              <span className="font-medium text-sm">Email</span>
            </button>
            <button
              onClick={handleCopyLink}
              className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg hover:bg-amber-500/10 text-white/80 hover:text-amber-300 transition-all"
              data-testid="share-copy-link"
            >
              {copied ? <Check className="w-5 h-5 text-green-400" /> : <Copy className="w-5 h-5 text-amber-400/60" />}
              <span className="font-medium text-sm">{copied ? 'Lien copié !' : 'Copier le lien'}</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShareButton;
